import { Autocomplete } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { AnalyticsCards } from "Common/Components/AnalyticsCards/AnalyticsCards";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { defaultInnerStyle } from "Common/Components/PageContainer/PageContainer";
import CustomStyle, { autocompleteDropdownStyles } from "Constants/Style";
import {
  Package,
  CircleCheckBig,
  TrendingUp,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MecordColors } from "Constants/Color";
import SupplierStockListing from "./SupplierStock/SupplierStockListing";
import StockOverviewListing from "./StockOverview/StockOverviewListing";
import { getWarehouseByUser } from "slices/Masters/warehosue/thunk";
import { clearAllTableStates } from "utils/clearTableStates";
import NoWarehousesMessage from "components/NoWarehouse/NoWarehouseMessage";

const InStockList = () => {
  const dispatch = useDispatch<any>();
  const [warehouseId, setWarehouseId] = useState<string | null>(() => {
    return sessionStorage.getItem("SelectedWarehouseId");
  });
  const [tab, setTab] = useState(
    Number(sessionStorage?.getItem("InventoryInstockTab")) || 1,
  );

  const { warehouseByUserList, warehouseByUserLoading } = useSelector(
    (state: any) => state.warehouse,
  );

  useEffect(() => {
    if (!warehouseByUserList?.length) {
      setWarehouseId(null);
      sessionStorage.removeItem("SelectedWarehouseId");
      return;
    }

    const savedWarehouseId = sessionStorage.getItem("SelectedWarehouseId");

    if (
      savedWarehouseId &&
      warehouseByUserList.some((w: any) => w.warehouse_id === savedWarehouseId)
    ) {
      setWarehouseId(savedWarehouseId);
    } else {
      const defaultWarehouseId = warehouseByUserList[0].warehouse_id;
      setWarehouseId(defaultWarehouseId);
      sessionStorage.setItem("SelectedWarehouseId", defaultWarehouseId);
    }
  }, [warehouseByUserList]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "SelectedWarehouseId") {
        setWarehouseId(event.newValue);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      const newTab = newValue + 1;
      setTab(newTab);
      clearAllTableStates();
      sessionStorage.setItem("InventoryInstockTab", newTab.toString());
    },
    [],
  );

  const statuses = useMemo(() => {
    if (tab === 1) {
      return [
        {
          label: "Total Items",
          icon: <Package size={16} />,
          bgColor: "bg-blue-50",
          textColor: "text-blue-600",
          count: 120,
        },
        {
          label: "Total Stock",
          icon: <Package size={16} />,
          bgColor: "bg-yellow-50",
          textColor: "text-yellow-600",
          count: 35,
        },
        {
          label: "Total Received",
          icon: <CircleCheckBig size={16} />,
          bgColor: "bg-green-50",
          textColor: "text-green-600",
          count: 50,
        },
        {
          label: "Avg Stock/item",
          icon: <TrendingUp size={16} />,
          bgColor: "bg-green-50",
          textColor: "text-green-600",
          count: 50,
        },
      ];
    }

    return [
      {
        label: "Total Suppliers",
        icon: <Package size={16} />,
        bgColor: "bg-blue-50",
        textColor: "text-blue-600",
        count: 15,
      },
      {
        label: "Total items",
        icon: <Package size={16} />,
        bgColor: "bg-yellow-50",
        textColor: "text-yellow-600",
        count: 8,
      },
      {
        label: "Total Supplier Stock",
        icon: <CircleCheckBig size={16} />,
        bgColor: "bg-green-50",
        textColor: "text-green-600",
        count: 12,
      },
      {
        label: "Avg Stock/Supplier",
        icon: <TrendingUp size={16} />,
        bgColor: "bg-green-50",
        textColor: "text-green-600",
        count: 95,
      },
    ];
  }, [tab]);

  useEffect(() => {
    dispatch(getWarehouseByUser());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("InventoryInstockTab");
    };
  }, []);

  return (
    <div
      className="page-content px-3 mecord-kpi-list-page"
      style={{ "--mecord-table-offset": "400px" } as React.CSSProperties}
    >
      <div className="flex-shrink-0">
        <AnalyticsCards statuses={statuses} />
      </div>

      <div
        className="mecord-list-panel"
        style={{
          ...defaultInnerStyle,
          flex: "1 1 0",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="flex-shrink-0">
          <div className="mecord-toolbar-title">
            <h6>In Stock</h6>
            <Autocomplete
              options={warehouseByUserList ?? []}
              loading={warehouseByUserLoading}
              getOptionLabel={(option: any) => option?.warehouse_name || ""}
              value={
                warehouseByUserList.find(
                  (opt: any) => opt.warehouse_id === warehouseId,
                ) ?? null
              }
              isOptionEqualToValue={(option: any, value: any) =>
                option.warehouse_id === value.warehouse_id
              }
              onChange={(_, selected) => {
                clearAllTableStates();
                const id = selected?.warehouse_id ?? null;
                setWarehouseId(id);

                if (id) {
                  sessionStorage.setItem("SelectedWarehouseId", id);
                } else {
                  sessionStorage.removeItem("SelectedWarehouseId");
                }
              }}
              disableClearable
              sx={{ ...CustomStyle?.singleselect, width: 280 }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option) =>
                autocompleteDropdownStyles.renderOption(
                  props,
                  option,
                  (opt) =>
                    typeof opt === "string"
                      ? opt
                      : String(opt.warehouse_name),
                )
              }
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  name="warehouse"
                  placeholder="Select Warehouse"
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: `${MecordColors?.ThemeBGColor} !important`,
                      },
                      "&:hover fieldset": {
                        borderColor: `${MecordColors?.ThemeBGColor} !important`,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: `${MecordColors?.ThemeBGColor} !important`,
                      },
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      height: 34,
                    },
                  }}
                />
              )}
            />
          </div>

          <div className="mecord-tabs-wrap">
            <Box sx={{ width: "100%" }}>
              <Tabs
                value={tab - 1}
                onChange={handleTabChange}
                sx={{
                  minHeight: 30,
                  "& .MuiTab-root": {
                    fontFamily: "'Poppins', sans-serif",
                    textTransform: "none",
                    color: "#555555",
                    minHeight: 30,
                    paddingTop: "2px",
                    paddingBottom: "2px",
                    transition: "color 0.2s ease",
                  },
                  "& .MuiTab-root:hover": {
                    color: "#d2363a",
                  },
                  "& .Mui-selected": {
                    color: "#d2363a !important",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#d2363a",
                    height: "3px",
                    transition: "all 0.3s ease",
                  },
                }}
              >
                <Tab label="Stock Overview" />
                <Tab label="Supplier Stock" />
              </Tabs>
              <Divider sx={{ mt: 0, borderColor: "#c35f61" }} />
            </Box>
          </div>
        </div>

        <div
          className="d-flex flex-column"
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            maxHeight: "100%",
            overflow: "hidden",
          }}
        >
          {warehouseByUserList.length === 0 ? (
            <NoWarehousesMessage />
          ) : tab === 1 ? (
            <StockOverviewListing
              key={`stockOverview-${warehouseId}`}
              warehouseId={warehouseId}
            />
          ) : tab === 2 ? (
            <SupplierStockListing
              key={`supplierStock-${warehouseId}`}
              warehouseId={warehouseId}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default InStockList;
